import React, { useState, useEffect } from 'react';
import { X, Loader, Info, Upload, Trash2 } from 'lucide-react';
import { uploadProfilePicture } from '../services/storageService';

const CaregiverProfileEditor = ({ isOpen, onClose, profile, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    bio: '',
    hourlyRate: 250,
    maxPets: 3,
    speciesAccepted: [],
    location: '',
    badges: [],
    isAvailable: true,
    isBackgroundChecked: false,
    homePhotos: []
  });
  
  const [uploading, setUploading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [error, setError] = useState('');
  const [photoPreview, setPhotoPreview] = useState(null);

  const availableSpecies = ['Dog', 'Cat', 'Bird', 'Rabbit', 'Hamster', 'Guinea Pig', 'Fish', 'Reptile'];
  
  const availableBadges = [
    { value: 'verified', label: 'Active' },
    { value: 'vetChecked', label: 'Pet First Aid Certified' },
    { value: 'superhost', label: 'Top Rated' },
    { value: 'trainer', label: 'Certified Trainer' },
    { value: 'catSpecialist', label: 'Cat Specialist' },
    { value: 'daycare', label: 'Daycare Provider' },
    { value: 'grooming', label: 'Grooming Services' },
    { value: 'walker', label: 'Professional Walker' },
    { value: 'boarding', label: 'Boarding Services' },
    { value: 'medical', label: 'Medical Care' },
    { value: 'senior-care', label: 'Senior Pet Care' },
    { value: 'puppy-specialist', label: 'Puppy Specialist' },
    { value: 'training', label: 'Training Services' }
  ];

  useEffect(() => {
    if (profile) {
      setFormData({
        title: profile.title || 'Professional Pet Caregiver',
        bio: profile.bio || '',
        hourlyRate: profile.hourlyRate || 250,
        maxPets: profile.maxPets || 3,
        speciesAccepted: profile.speciesAccepted || ['Dog', 'Cat'],
        location: profile.location || '',
        badges: profile.badges || ['verified'],
        isAvailable: profile.isAvailable !== false,
        isBackgroundChecked: profile.isBackgroundChecked || false,
        homePhotos: profile.homePhotos || []
      });
    }
  }, [profile]);

  const handleSpeciesToggle = (species) => {
    setFormData(prev => ({
      ...prev,
      speciesAccepted: prev.speciesAccepted.includes(species)
        ? prev.speciesAccepted.filter(s => s !== species)
        : [...prev.speciesAccepted, species]
    }));
  };

  const handleBadgeToggle = (badge) => {
    setFormData(prev => ({
      ...prev,
      badges: prev.badges.includes(badge)
        ? prev.badges.filter(b => b !== badge)
        : [...prev.badges, badge]
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhotos(true);
    setError('');

    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        // Validate file
        if (file.size > 5 * 1024 * 1024) {
          setError(`${file.name} is too large. Maximum 5MB per image.`);
          continue;
        }

        if (!file.type.startsWith('image/')) {
          setError(`${file.name} is not a valid image.`);
          continue;
        }

        // Upload to Firebase Storage
        const result = await uploadProfilePicture(profile.userId, file);
        if (result.success) {
          uploadedUrls.push(result.url);
        } else {
          setError(`Failed to upload ${file.name}: ${result.error}`);
        }
      }

      if (uploadedUrls.length > 0) {
        setFormData(prev => ({
          ...prev,
          homePhotos: [...prev.homePhotos, ...uploadedUrls]
        }));
      }
    } catch (err) {
      setError('Failed to upload photos: ' + err.message);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleRemovePhoto = (photoUrl) => {
    setFormData(prev => ({
      ...prev,
      homePhotos: prev.homePhotos.filter(url => url !== photoUrl)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.title.trim()) {
      setError('Professional title is required');
      return;
    }

    if (!formData.bio.trim()) {
      setError('Bio is required');
      return;
    }

    if (formData.hourlyRate < 50) {
      setError('Hourly rate must be at least ₹50');
      return;
    }

    if (formData.maxPets < 1) {
      setError('Maximum pets must be at least 1');
      return;
    }

    if (formData.speciesAccepted.length === 0) {
      setError('Please select at least one pet species you accept');
      return;
    }

    setUploading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError('Failed to save profile: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full">
          {/* Header - Sticky */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl z-10">
            <h2 className="text-2xl font-bold text-gray-900">Edit Professional Profile</h2>
            <button 
              onClick={onClose} 
              disabled={uploading}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <X size={24} className="text-gray-600" />
            </button>
          </div>

          {/* Form - Scrollable */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(90vh-160px)] overflow-y-auto">
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-2">
                <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Complete your professional profile</p>
                  <p className="text-blue-800">This information will be displayed to pet owners when they search for caregivers. Make sure to fill out all details accurately!</p>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
                {error}
              </div>
            )}

            {/* Professional Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Professional Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent transition-all"
                placeholder="e.g., Professional Dog Caregiver & Trainer"
                maxLength={80}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.title.length}/80 characters</p>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                About You *
              </label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent transition-all h-32"
                placeholder="Tell pet owners about your experience, qualifications, and what makes you a great caregiver..."
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">{formData.bio.length}/500 characters</p>
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent transition-all"
                placeholder="e.g., Bangalore, Koramangala"
              />
            </div>

            {/* Pricing & Capacity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Base/Starting Rate (₹/hr) *
                </label>
                <input
                  type="number"
                  value={formData.hourlyRate}
                  onChange={(e) => setFormData({ ...formData, hourlyRate: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent transition-all"
                  min="50"
                  step="50"
                  placeholder="Your minimum rate"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Displayed as your starting price. Set individual service rates in "My Services"
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Maximum Pets *
                </label>
                <input
                  type="number"
                  value={formData.maxPets}
                  onChange={(e) => setFormData({ ...formData, maxPets: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[var(--pf-accent)] focus:border-transparent transition-all"
                  min="1"
                  max="10"
                />
              </div>
            </div>

            {/* Species Accepted */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Pet Species You Accept *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {availableSpecies.map((species) => (
                  <button
                    key={species}
                    type="button"
                    onClick={() => handleSpeciesToggle(species)}
                    className={`px-4 py-3 rounded-xl border-2 font-medium transition-all ${
                      formData.speciesAccepted.includes(species)
                        ? 'border-[var(--pf-accent)] bg-[var(--pf-accent-soft)] text-[var(--pf-accent)]'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {species}
                  </button>
                ))}
              </div>
            </div>

            {/* Badges/Certifications */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Badges & Certifications
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableBadges.map((badge) => (
                  <button
                    key={badge.value}
                    type="button"
                    onClick={() => handleBadgeToggle(badge.value)}
                    className={`px-4 py-3 rounded-xl border-2 font-medium transition-all text-left ${
                      formData.badges.includes(badge.value)
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {badge.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Home Photos */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Home Photos (Show pet owners your space)
              </label>
              
              {/* Upload Button */}
              <label className="cursor-pointer">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[var(--pf-accent)] hover:bg-[var(--pf-accent-soft)] transition-all">
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">
                    {uploadingPhotos ? 'Uploading...' : 'Click to upload home photos'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF or WebP • Max 5MB per image</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhotos}
                  className="hidden"
                />
              </label>

              {/* Photo Grid */}
              {formData.homePhotos.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-4">
                  {formData.homePhotos.map((photo, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photo}
                        alt={`Home ${index + 1}`}
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(photo)}
                        className="absolute top-1 right-1 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Availability & Background Check */}
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isAvailable}
                  onChange={(e) => setFormData({ ...formData, isAvailable: e.target.checked })}
                  className="w-5 h-5 text-[var(--pf-accent)] border-gray-300 rounded focus:ring-[var(--pf-accent)]"
                />
                <span className="text-sm font-medium text-gray-700">
                  I am currently available for bookings
                </span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isBackgroundChecked}
                  onChange={(e) => setFormData({ ...formData, isBackgroundChecked: e.target.checked })}
                  className="w-5 h-5 text-[var(--pf-accent)] border-gray-300 rounded focus:ring-[var(--pf-accent)]"
                />
                <span className="text-sm font-medium text-gray-700">
                  I have completed a background check
                </span>
              </label>
            </div>
          </form>

          {/* Action Buttons - Sticky */}
          <div className="flex gap-3 p-6 border-t border-gray-200 sticky bottom-0 bg-white rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={uploading || uploadingPhotos}
              className="flex-1 px-6 py-3 pf-btn-primary rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  Saving...
                </>
              ) : (
                'Save Professional Profile'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CaregiverProfileEditor;

